import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Shield,
  FileText,
  Cookie,
  Scale,
  ChevronRight,
  Clock,
  Mail,
  Lock,
  User,
  Store,
  ShoppingBag,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import MobileBottomNav from '../components/layout/MobileBottomNav';

const LegalPages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState('privacy');
  const [lastUpdated, setLastUpdated] = useState('January 1, 2024');

  const brandColor = '#ff660e';
  const brandColorLight = '#fff0e6';

  // Set active tab based on URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['privacy', 'terms', 'cookies', 'legal'].includes(hash)) {
      setActiveTab(hash);
    }
  }, [location]);

  const privacyPolicy = {
    title: "Privacy Policy",
    icon: <Shield className="w-6 h-6" />,
    sections: [
      {
        title: "1. Information We Collect",
        content: `
          When you use EcoMart, we collect the following types of information:
          
          • Personal Information: Name, email address, phone number, and profile information
          • Transaction Information: Purchase history, payment details (processed securely through our escrow system)
          • Usage Data: How you interact with our platform, pages visited, and features used
          • Device Information: IP address, browser type, and device identifiers
          • Location Data: General location information for localized marketplace features
        `
      },
      {
        title: "2. How We Use Your Information",
        content: `
          We use your information to:
          
          • Provide and maintain our marketplace services
          • Process transactions through our secure escrow system
          • Verify user identities and prevent fraud
          • Communicate important updates and support responses
          • Improve platform features and user experience
          • Comply with legal obligations
        `
      },
      {
        title: "3. Information Sharing",
        content: `
          We never sell your personal data. We only share information when:
          
          • Required for transaction completion (e.g., buyer-seller communication)
          • Necessary for payment processing through secure partners
          • Required by law or legal processes
          • To protect the rights and safety of our community
          • With your explicit consent for specific purposes
        `
      },
      {
        title: "4. Data Security",
        content: `
          We implement industry-standard security measures:
          
          • End-to-end encryption for sensitive data
          • Regular security audits and monitoring
          • Secure payment processing
          • Limited employee access to personal information
          • Two-factor authentication options
        `
      },
      {
        title: "5. Your Rights",
        content: `
          You have the right to:
          
          • Access your personal data
          • Correct inaccurate information
          • Request data deletion (subject to legal requirements)
          • Opt-out of marketing communications
          • Export your data in a portable format
          • Withdraw consent where applicable
        `
      },
      {
        title: "6. Contact Information",
        content: `
          For privacy-related inquiries or to exercise your rights:
          
          Email: privacy@ecomart.com
          Address: 123 Sustainable Street, Green Valley, CA 94107
          Response Time: We aim to respond within 30 days
        `
      }
    ]
  };

  const termsOfService = {
    title: "Terms of Service",
    icon: <FileText className="w-6 h-6" />,
    sections: [
      {
        title: "1. Acceptance of Terms",
        content: `
          By accessing or using EcoMart, you agree to be bound by these Terms of Service. 
          If you disagree with any part, you may not access our services.
        `
      },
      {
        title: "2. User Accounts",
        content: `
          • You must be at least 18 years old to use EcoMart
          • Provide accurate and complete registration information
          • Maintain the security of your account credentials
          • Notify us immediately of unauthorized account access
          • One account per individual unless authorized
        `
      },
      {
        title: "3. Marketplace Rules",
        content: `
          All users must comply with these marketplace rules:
          
          • List only items you legally own and can sell
          • Provide accurate item descriptions and photos
          • Use our escrow system for all transactions
          • No prohibited items (weapons, illegal substances, etc.)
          • Fair pricing and honest negotiation
          • Respect all community members
        `
      },
      {
        title: "4. Transactions & Escrow",
        content: `
          • All payments must go through EcoMart's escrow system
          • Direct payments outside our platform are prohibited
          • Ensure your satisfy before releasing funds
          • Report any issues encountered during inspection
          • We may hold funds during dispute resolution
        `
      },
      {
        title: "5. Prohibited Activities",
        content: `
          You may not:
          
          • Engage in fraud, scams, or deceptive practices
          • List counterfeit or stolen goods
          • Harass other users
          • Circumvent our fees or payment systems
          • Use automated tools to scrape data
          • Violate any laws through our platform
        `
      },
      {
        title: "6. Limitation of Liability",
        content: `
          EcoMart provides a platform for transactions but is not a party to user agreements.
          We are not liable for:
          
          • User disputes or transaction issues
          • Product quality or delivery problems
          • User-generated content
          • Third-party services integrated with our platform
          • Unauthorized account access due to user negligence
        `
      },
      {
        title: "7. Termination",
        content: `
          We may suspend or terminate accounts for:
          
          • Violation of these terms
          • Fraudulent activity
          • Creating multiple accounts for abuse
          • Legal or regulatory requirements
          • Platform security concerns
        `
      }
    ]
  };

  const cookiePolicy = {
    title: "Cookie Policy",
    icon: <Cookie className="w-6 h-6" />,
    sections: [
      {
        title: "1. What Are Cookies?",
        content: `
          Cookies are small text files stored on your device when you visit websites. 
          They help websites remember your preferences and improve your browsing experience.
        `
      },
      {
        title: "2. How We Use Cookies",
        content: `
          Essential Cookies:
          • Session management and security
          • User authentication
          • Transaction processing
          
          Functional Cookies:
          • Remembering your preferences
          • Language settings
          • Display preferences
          
          Analytics Cookies:
          • Understanding how users interact with our platform
          • Improving features and usability
          • Measuring performance
        `
      },
      {
        title: "3. Third-Party Cookies",
        content: `
          We work with trusted partners who may set cookies:
          
          • Payment processors for secure transactions
          • Analytics providers to improve our services
          • Advertising partners (only with user consent)
          • Customer support platforms
        `
      },
      {
        title: "4. Cookie Management",
        content: `
          You can control cookies through:
          
          • Browser settings (disable, delete, or block cookies)
          • Our cookie consent banner when you first visit
          • Platform settings for non-essential cookies
          
          Note: Disabling essential cookies may limit platform functionality.
        `
      },
      {
        title: "5. Data Retention",
        content: `
          • Session cookies: Deleted when you close your browser
          • Persistent cookies: Up to 24 months for functional purposes
          • Analytics data: Aggregated and anonymized after 36 months
          • User preferences: Stored until you change them
        `
      }
    ]
  };

  const legal = {
    title: "Legal Information",
    icon: <Scale className="w-6 h-6" />,
    sections: [
      {
        title: "1. Company Information",
        content: `
          EcoMart Nigeria
          Operating under: SalsTech IT Solutions (RC: 7398490)
          Registered Address: Lagos, Nigeria
          Company Registration: RC 7398490
          VAT/TIN: As registered with FIRS Nigeria
        `
      },
      {
        title: "2. Intellectual Property",
        content: `
          • EcoMart name, logo, and platform design are trademarks of SalsTech IT Solutions
          • User-generated content remains the intellectual property of the respective users
          • Platform code, features, and architecture are proprietary to SalsTech IT Solutions
          • Report copyright infringement to copyright@ecomart.ng with subject line: "DMCA Notice"
        `
      },
      {
        title: "3. Governing Law & Jurisdiction",
        content: `
          These legal documents are governed by and construed in accordance with the laws of the Federal Republic of Nigeria.
          
          Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of Lagos State, Nigeria.
          
          For international users, Nigerian law shall apply to all transactions conducted on the EcoMart platform.
        `
      },
      {
        title: "4. Regulatory Compliance",
        content: `
          EcoMart Nigeria complies with:
          
          • Nigeria Data Protection Regulation (NDPR) 2019
          • Companies and Allied Matters Act (CAMA) 2020
          • Federal Inland Revenue Service (FIRS) regulations
          • Consumer Protection Council (CPC) guidelines
          • Central Bank of Nigeria (CBN) payment regulations
          • Nigerian Communications Commission (NCC) data protection requirements
        `
      },
      {
        title: "5. Dispute Resolution",
        content: `
          • All disputes shall be addressed through our internal resolution process
          • Escrow disputes are resolved through our internal mediation team within 72 hours
        `
      },
      {
        title: "6. Service Availability",
        content: `
          • EcoMart services are primarily available within Nigeria
          • International transactions may be subject to additional terms and restrictions
          • We comply with Nigerian export and import regulations
          • Currency: All transactions are conducted in Nigerian Naira (₦)
        `
      },
      {
        title: "7. Updates & Notifications",
        content: `
          • Significant policy changes will be notified via email and platform notifications
          • Updated versions immediately supersede previous agreements
          • Continued use of EcoMart constitutes acceptance of current terms
          • Major changes include a 30-day notice period for user review
          • Users are responsible for regularly reviewing legal documents
        `
      },
      {
        title: "8. User Responsibilities",
        content: `
          • Users must comply with all applicable Nigerian laws and regulations
          • Users are responsible for accurate tax reporting to relevant Nigerian authorities
          • International users must ensure compliance with their local laws when using EcoMart
          • All users must verify their identity according to Nigerian KYC requirements
        `
      },
      {
        title: "9. Data Protection",
        content: `
          • We comply with Nigeria Data Protection Regulation (NDPR) 2019
          • User data is stored on servers located in Nigeria
          • Data transfer outside Nigeria follows NDPR guidelines
          • Users have rights to access, correct, and delete their personal data
        `
      }
    ]
  };

  const getActiveContent = () => {
    switch (activeTab) {
      case 'privacy': return privacyPolicy;
      case 'terms': return termsOfService;
      case 'cookies': return cookiePolicy;
      case 'legal': return legal;
      default: return privacyPolicy;
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/legal#${tab}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6"
               style={{ backgroundColor: brandColorLight }}>
            <div className="flex items-center">
              <Scale className="w-8 h-8 ml-1" style={{ color: brandColor }} />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Legal & Policies
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Important information about how we protect your data and govern our marketplace
          </p>
          <div className="mt-4 text-sm text-gray-500">
            Last Updated: {lastUpdated}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-2xl shadow-sm p-2 mb-8">
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'privacy', label: 'Privacy Policy', icon: <Shield className="w-5 h-5" /> },
              { id: 'terms', label: 'Terms of Service', icon: <FileText className="w-5 h-5" /> },
              { id: 'cookies', label: 'Cookie Policy', icon: <Cookie className="w-5 h-5" /> },
              { id: 'legal', label: 'Legal Information', icon: <Scale className="w-5 h-5" /> }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                style={{ 
                  backgroundColor: activeTab === tab.id ? brandColor : 'transparent'
                }}
              >
                {tab.icon}
                <span className="ml-2">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Document Header */}
          <div className="border-b border-gray-200 p-6 md:p-8">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4"
                   style={{ backgroundColor: brandColorLight }}>
                <div style={{ color: brandColor }}>{getActiveContent().icon}</div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{getActiveContent().title}</h2>
                <p className="text-gray-600 mt-1">
                  This document explains our policies and your rights as an EcoMart user
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center text-sm text-gray-500">
                <Clock className="w-4 h-4 mr-2" />
                Updated {lastUpdated}
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/contact')}
                  className="text-sm font-medium"
                  style={{ color: brandColor }}
                >
                  Have Questions?
                </button>
              </div>
            </div>
          </div>

          {/* Document Content */}
          <div className="p-6 md:p-8">
            {/* Quick Summary */}
            <div className="mb-10 p-6 rounded-xl"
                 style={{ backgroundColor: brandColorLight }}>
              <div className="flex items-start">
                <AlertCircle className="w-6 h-6 mr-3 mt-1 flex-shrink-0" style={{ color: brandColor }} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Important Notice</h3>
                  <p className="text-gray-700">
                    By using EcoMart, you agree to these terms. We recommend reading this document 
                    carefully as it affects your rights and obligations.
                  </p>
                </div>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-10">
              {getActiveContent().sections.map((section, index) => (
                <div key={index} className="border-b border-gray-100 pb-10 last:border-0">
                  <div className="flex items-start mb-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mr-4"
                         style={{ backgroundColor: brandColorLight }}>
                      <span className="font-bold text-sm" style={{ color: brandColor }}>
                        {index + 1}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                  </div>
                  <div className="ml-12">
                    <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                      {section.content}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Acceptance */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Acceptance of Terms</h3>
                  <p className="text-gray-600">
                    By continuing to use EcoMart, you acknowledge that you have read, understood, 
                    and agree to be bound by these terms.
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-gray-600">Your acceptance is recorded</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-12">
          <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Related Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <button
              onClick={() => navigate('/how-to-buy')}
              className="bg-white rounded-xl shadow-sm p-6 text-left hover:shadow-md transition-shadow group"
            >
              <div className="flex items-center mb-4">
                <ShoppingBag className="w-6 h-6 mr-3" style={{ color: brandColor }} />
                <h4 className="font-semibold text-gray-900 group-hover:text-orange-600">Buying Guide</h4>
              </div>
              <p className="text-gray-600 text-sm mb-3">Learn how to buy safely on EcoMart</p>
              <span className="text-sm font-medium" style={{ color: brandColor }}>Read Guide →</span>
            </button>
            
            <button
              onClick={() => navigate('/how-to-sell')}
              className="bg-white rounded-xl shadow-sm p-6 text-left hover:shadow-md transition-shadow group"
            >
              <div className="flex items-center mb-4">
                <Store className="w-6 h-6 mr-3" style={{ color: brandColor }} />
                <h4 className="font-semibold text-gray-900 group-hover:text-orange-600">Selling Guide</h4>
              </div>
              <p className="text-gray-600 text-sm mb-3">Best practices for selling securely</p>
              <span className="text-sm font-medium" style={{ color: brandColor }}>Read Guide →</span>
            </button>
            
            <button
              onClick={() => navigate('/escrow')}
              className="bg-white rounded-xl shadow-sm p-6 text-left hover:shadow-md transition-shadow group"
            >
              <div className="flex items-center mb-4">
                <Lock className="w-6 h-6 mr-3" style={{ color: brandColor }} />
                <h4 className="font-semibold text-gray-900 group-hover:text-orange-600">Escrow Guide</h4>
              </div>
              <p className="text-gray-600 text-sm mb-3">How our payment protection works</p>
              <span className="text-sm font-medium" style={{ color: brandColor }}>Read Guide →</span>
            </button>
          </div>
        </div>

        {/* Contact Support */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-8">
          <div className="text-center">
            <Mail className="w-12 h-12 mx-auto mb-6" style={{ color: brandColor }} />
            <h3 className="text-xl font-bold text-gray-900 mb-4">Questions About Our Policies?</h3>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              If you have questions about our legal documents or need clarification on any terms, 
              our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center"
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact Support
              </button>
              <button
                onClick={() => navigate('/faq')}
                className="px-6 py-3 border-2 border-white text-blue-600 font-semibold rounded-lg hover:bg-white/50 transition-colors"
              >
                Visit FAQ
              </button>
            </div>
          </div>
        </div>

      </div>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default LegalPages;